import React, { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { notNil } from '../../../core/helpers/is-nil';
import { DefinitionList } from '../../../visual-components/definition-list/definition-list';
import { DefinitionBasic } from '../../../visual-components/definition-list/definition-renderers/definition-basic/definition-basic';
import { InPageLoader } from '../../../visual-components/in-page-loader/in-page-loader';
import { QueryErrorHandler } from '../../../visual-components/query-error-handler/query-error-handler';
import { GetCustomerResponse } from '../../backend/openapi/api-types';
import { DeliveryAddressDialog } from '../../../visual-components/delivery-address-dialog/delivery-address-dialog';
import { useDeliveryAddress } from './hooks/use-delivery-address';

type Props = {
  customerData: GetCustomerResponse['data'] | undefined;
  isLoading: boolean;
  readOnly?: boolean;
  error?: ComponentProps<typeof QueryErrorHandler>['error'];
};

export const CartDetails: React.FC<Props> = ({ customerData, isLoading, readOnly = false, error }) => {
  const { t } = useTranslation();
  const hasCustomerData = notNil(customerData);

  const { selectedDeliveryAddress, setShippingAddressId } = useDeliveryAddress(customerData?.shippingAddresses);

  if (isLoading || !hasCustomerData) {
    return <InPageLoader />;
  }

  return (
    <DefinitionList debugInfo="Error fetching customer data" error={error} title={t('cart.orderDetails')}>
      <DeliveryAddressDialog
        addresses={customerData.shippingAddresses}
        readOnly={readOnly}
        selectedDeliveryAddress={selectedDeliveryAddress}
        onDialogConfirm={setShippingAddressId}
      />
      <DefinitionBasic term={t('global.invoiceAddress')}>{customerData.invoiceAddress.addressString}</DefinitionBasic>
      <DefinitionBasic term={t('cart.paymentConditions.title')}>{t('cart.paymentConditions.text')}</DefinitionBasic>
    </DefinitionList>
  );
};
